import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {SliceComponentProps} from '@prismicio/react'
import cn from 'classnames'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import {getButtonsData} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'
import type {Context} from 'src/common/types'

export type ExplainerImgPlusVerticalTilesProps = SliceComponentProps<Content.ExplainerImgPlusVerticalTilesSlice>

const bgMap: Record<string, string> = {
  'Dark green': 'bg-green-dark',
  'Grey/100': 'bg-moss-grey-100',
  'Moss White': 'bg-moss-white',
}

const ExplainerImgPlusVerticalTiles = ({
  slice,
  context: {pageUid, buttonsData},
}: ExplainerImgPlusVerticalTilesProps & {context: Context}): JSX.Element => {
  const {linkResolver} = useLinkResolver()
  const domainCountry = useGetDomainCountry()

  const {primaryButtonData: buttonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.button,
  })

  const buttonHref = buttonData?.link ? linkResolver(buttonData.link, 'explainer-cta') : null
  const buttonLabel = buttonData?.label

  const trackButtonClick = () => {
    trackMossAnalytics({
      event: 'interaction',
      interaction: {
        element: buttonData?.marketing_id || 'explainer-img-tiles-cta',
        elementText: buttonLabel || '',
        location: 'explainer-img-tiles-cta',
        type: 'click',
      },
      page: {domainCountry, type: pageUid || 'unknown'},
    })
  }

  const p = slice.primary
  const isDark = p.background_color === 'Dark green'
  const bgClass = bgMap[p.background_color ?? 'Dark green'] ?? 'bg-green-dark'
  const ptClass = PADDING_TOP_CLASS[p.padding_top ?? DEFAULT_PADDING] ?? ''
  const pbClass = PADDING_BOTTOM_CLASS[p.padding_bottom ?? DEFAULT_PADDING] ?? ''
  const hasButton = buttonHref && buttonLabel

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      data-nav-dark={isDark || undefined}
      className={cn(bgClass, ptClass, pbClass, isDark ? 'text-moss-white' : 'text-moss-black')}
    >
      <div className="mx-auto max-w-[1280px] px-5 md:px-10">
        {/* Header: eyebrow + heading + description */}
        <div className="mb-14">
          {p.eyebrow && (
            <p
              className={cn(
                'text-[11px] font-semibold uppercase tracking-widest',
                isDark ? 'text-green-neon' : 'text-green-deep',
              )}
            >
              {p.eyebrow}
            </p>
          )}
          {p.heading && (
            <h2 className={cn('text-h3 md:text-h2 mb-5 mt-4', isDark ? 'text-moss-white' : 'text-moss-black')}>
              {p.heading}
            </h2>
          )}
          {p.description && (
            <p
              className={cn(
                'max-w-[680px] text-[19px] leading-relaxed',
                isDark ? 'text-moss-white/70' : 'text-black/65',
              )}
            >
              {p.description}
            </p>
          )}
        </div>

        {/* Body: image (sticky) | tiles */}
        <div className="grid items-start gap-6 md:grid-cols-2 md:gap-8">
          {/* Image with caption — sticky on desktop so it follows scroll when tiles are taller */}
          <div className="relative max-h-[400px] overflow-hidden rounded-xl md:sticky md:top-20 md:max-h-[480px]">
            {isFilled.image(p.image) && (
              <PrismicNextImage
                field={p.image}
                className="size-full object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            )}
            {p.image_caption && (
              <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-black/70 px-4 py-2 backdrop-blur-sm">
                <span className="size-2 shrink-0 rounded-full bg-green-neon" />
                <span className="text-[13px] font-medium text-moss-white">{p.image_caption}</span>
              </div>
            )}
          </div>

          {/* Right column: tiles + static CTA at bottom */}
          <div>
            <div className="flex flex-col gap-4">
              {p.tiles.map((tile, i) => (
                <div
                  key={i}
                  className={cn(
                    'flex flex-col rounded-lg border p-7.5',
                    isDark ? 'border-moss-white/[0.08] bg-moss-white/[0.05]' : 'border-black/[0.08] bg-black/[0.03]',
                  )}
                >
                  {isFilled.image(tile.icon) && (
                    <div
                      className={cn(
                        'flex size-10 items-center justify-center rounded-[10px]',
                        isDark ? 'bg-moss-white/[0.08]' : 'bg-black/[0.08]',
                      )}
                      title={tile.tooltip ?? undefined}
                    >
                      <PrismicNextImage field={tile.icon} className="size-5 brightness-0 invert" sizes="20px" />
                    </div>
                  )}
                  <h3 className="mt-3 text-22 font-semibold leading-snug md:text-24">{tile.title}</h3>
                  <p
                    className={cn(
                      'mt-1 text-[14px] leading-relaxed md:text-[15px]',
                      isDark ? 'text-moss-white/60' : 'text-black/55',
                    )}
                  >
                    {tile.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Static CTA below tiles */}
            {hasButton && (
              <div className="mt-6 flex flex-col items-center gap-4 md:flex-row md:justify-end md:gap-4">
                {p.button_helper_text && (
                  <span className={cn('text-[14px]', isDark ? 'text-moss-white/60' : 'text-black/60')}>
                    {p.button_helper_text}
                  </span>
                )}
                <Link
                  href={buttonHref}
                  className="text-button-s inline-flex h-12 w-full shrink-0 items-center justify-center rounded-[4px] bg-green-neon px-4 py-3 uppercase text-black hover:bg-neon-550 md:w-auto"
                  onClick={trackButtonClick}
                >
                  {buttonLabel}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ExplainerImgPlusVerticalTiles
